// ============================================
// NATIVE MAPBOX VENUE LAYER
// High-performance venue rendering using Mapbox GL native layers
// ============================================

import { useEffect, useCallback, useRef } from 'react';

// Icon filename mapping (matching normalized icons in /public/icons/)
const ICON_MAP = {
  // Bars & Nightlife
  'vt_bar': 'beer128.png',
  'vt_pub': 'beer128.png',
  'vt_cocktail_bar': 'cocktailbar1024.png',
  'vt_wine_bar': 'wine128.png',
  'vt_shisha_lounge': 'shisha128.png',
  'vt_hookah': 'shisha128.png',
  'vt_brewpub': 'beer128.png',
  
  // Cafes & Coffee
  'vt_cafe': 'coffee_128.png',
  'vt_coffee_shop': 'coffee_128.png',
  'vt_tea_house': 'tea128.png',
  'vt_tea_stall': 'tea128.png',
  
  // Restaurants
  'vt_restaurant': 'restaurant_128.png',
  'vt_fine_dining': 'restaurant_128.png',
  'vt_fast_food': 'burger_fastfood128.png',
  'vt_burger': 'burger_fastfood128.png',
  'vt_diner': 'restaurant_128.png',
  'vt_seafood': 'restaurant_128.png',
  'vt_barbecue': 'restaurant_128.png',
  
  // Vegetarian/Vegan
  'vt_vegetarian': 'veg_eleaf_128.png',
  'vt_vegan': 'vegan_restaurant.png',
  
  // Desserts & Sweets
  'vt_dessert': 'dessert128.png',
  'vt_ice_cream': 'dessert128.png',
  'vt_bakery': 'bread128.png',
  'vt_sweet_shop': 'dessert128.png',
  
  // Juice & Beverages
  'vt_juice_bar': 'juice128.png',
  'vt_smoothie': 'juice128.png',
  
  // Street Food
  'vt_street_food': 'streetfood128.png',
  'vt_food_cart': 'streetfood128.png',
  'vt_food_truck': 'streetfood128.png',
  'vt_street_vendor': 'streetfood128.png',
  'vt_shawarma': 'wrap128.png',
  'vt_falafel': 'samosa128.png',
  'vt_kebab': 'streetfood128.png',
  
  // Fitness
  'vt_gym': 'gym128.png',
  'vt_fitness': 'gym128.png',
  'vt_crossfit': 'gym128.png',
  'vt_yoga': 'yoga_128.png',
  'vt_pilates': 'pilates_128.png',
  
  // Work & Business
  'vt_coworking': 'laptop128.png',
  'vt_office': 'laptop128.png',
  
  // Beach & Outdoor
  'vt_beach': 'beach128.png',
  'vt_beach_club': 'beach128.png',
  
  // Health
  'vt_pharmacy': 'pharmacy128.png',
  
  // Shopping
  'vt_shop': 'shopping_bag_128.png',
  'vt_retail': 'shopping_bag_128.png',
  
  // Default
  'default': 'default_pin.png'
};

// Get icon name from venue type
const getIconName = (venueType) => {
  if (!venueType) return 'default';
  
  const type = venueType.toLowerCase().trim();
  
  // Direct match
  if (ICON_MAP[type]) return type;
  
  // Pattern matching
  if (type.includes('bar') || type.includes('pub')) return 'vt_bar';
  if (type.includes('coffee') || type.includes('cafe')) return 'vt_cafe';
  if (type.includes('tea')) return 'vt_tea_house';
  if (type.includes('restaurant') || type.includes('dining')) return 'vt_restaurant';
  if (type.includes('fast') || type.includes('burger')) return 'vt_fast_food';
  if (type.includes('vegan')) return 'vt_vegan';
  if (type.includes('vegetarian') || type.includes('veg')) return 'vt_vegetarian';
  if (type.includes('dessert') || type.includes('sweet') || type.includes('ice cream')) return 'vt_dessert';
  if (type.includes('juice') || type.includes('smoothie')) return 'vt_juice_bar';
  if (type.includes('street') || type.includes('cart') || type.includes('stall') || type.includes('vendor')) return 'vt_street_food';
  if (type.includes('shawarma') || type.includes('wrap')) return 'vt_shawarma';
  if (type.includes('gym') || type.includes('fitness')) return 'vt_gym';
  if (type.includes('yoga')) return 'vt_yoga';
  if (type.includes('pilates')) return 'vt_pilates';
  if (type.includes('cowork') || type.includes('office')) return 'vt_coworking';
  if (type.includes('beach')) return 'vt_beach';
  if (type.includes('pharmacy')) return 'vt_pharmacy';
  if (type.includes('shisha') || type.includes('hookah')) return 'vt_shisha_lounge';
  if (type.includes('cocktail')) return 'vt_cocktail_bar';
  if (type.includes('wine')) return 'vt_wine_bar';
  if (type.includes('bakery') || type.includes('bread')) return 'vt_bakery';
  
  return 'default';
};

// Convert venues array to GeoJSON
export const venuesToGeoJSON = (venues, wmSlugsSet = new Set()) => {
  return {
    type: 'FeatureCollection',
    features: venues
      .filter(venue => {
        // Must have valid coordinates
        const lat = parseFloat(venue.lat);
        const lng = parseFloat(venue.lng);
        return lat && lng && !isNaN(lat) && !isNaN(lng);
      })
      .map(venue => {
        const lat = parseFloat(venue.lat);
        const lng = parseFloat(venue.lng);
        const iconType = getIconName(venue.venue_type || venue.venue_category);

        return {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          },
          properties: {
            id: venue._id || venue.id,
            slug: venue.slug || null,
            name: venue.name || 'Unknown Venue',
            venue_type: venue.venue_type || '',
            venue_category: venue.venue_category || '',
            icon: iconType,
            rating: venue.google_rating || 0,
            wm_wifi: wmSlugsSet.has(venue.slug || '') ? 1 : 0,
          }
        };
      })
  };
};

// Hook to manage native Mapbox venue layer
export const useNativeVenueLayer = (mapRef, venues, isMapLoaded, onVenueClick, wmSlugsSet = new Set()) => {
  const iconsLoadedRef = useRef(false);
  const sourceAddedRef = useRef(false);
  const clickHandlerAddedRef = useRef(false);
  const onVenueClickRef = useRef(onVenueClick); // ✅ Store callback in ref to avoid stale closures
  
  // Keep callback ref up to date
  useEffect(() => {
    onVenueClickRef.current = onVenueClick;
  }, [onVenueClick]);
  
  // Load all icons into the map
  const loadIcons = useCallback(async (map) => {
    if (iconsLoadedRef.current) {
      console.log('⏭️ Icons already loaded, skipping');
      return;
    }
    
    const iconNames = [...new Set(Object.values(ICON_MAP))];
    console.log(`🎨 Loading ${iconNames.length} icons...`);
    
    let loadedCount = 0;
    let errorCount = 0;
    
    const loadIcon = (iconFile) => {
      return new Promise((resolve) => {
        const iconName = iconFile.replace('.png', '');
        
        // Skip if already loaded
        if (map.hasImage(iconName)) {
          loadedCount++;
          resolve();
          return;
        }
        
        // Add timeout in case loadImage never calls callback
        const timeoutId = setTimeout(() => {
          console.warn(`⏰ Timeout loading icon ${iconFile}`);
          errorCount++;
          resolve();
        }, 5000);
        
        map.loadImage(`/icons/${iconFile}`, (error, image) => {
          clearTimeout(timeoutId);
          
          if (error) {
            errorCount++;
            console.warn(`Failed to load icon ${iconFile}:`, error.message || error);
            resolve();
            return;
          }
          
          if (!map.hasImage(iconName)) {
            try {
              map.addImage(iconName, image, { sdf: false });
            } catch (e) {
              console.warn(`Failed to add icon ${iconFile}:`, e.message);
            }
          }
          loadedCount++;
          resolve();
        });
      });
    };
    
    try {
      await Promise.all(iconNames.map(loadIcon));
      iconsLoadedRef.current = true;
      console.log(`✅ Loaded ${loadedCount}/${iconNames.length} venue icons (${errorCount} errors)`);
    } catch (error) {
      console.error('Error loading icons:', error);
    }
  }, []);
  
  // Add/update venue source and layer
  const updateVenueLayer = useCallback((map, venueData, wmSlugsSetLocal = new Set()) => {
    const sourceId = 'venues-source';
    const layerId = 'venues-layer';
    
    // Update or add source
    const source = map.getSource(sourceId);
    if (source) {
      source.setData(venueData);
    } else {
      map.addSource(sourceId, {
        type: 'geojson',
        data: venueData,
        cluster: true, // ✅ ENABLED: Native clustering for performance
        clusterMaxZoom: 14, // Cluster until zoom 14, then show individual venues
        clusterRadius: 50 // Radius of each cluster when clustering
      });
      sourceAddedRef.current = true;
    }
    
    // Add layer if not exists
    if (!map.getLayer(layerId)) {
      // ✅ Add cluster circle layer
      map.addLayer({
        id: 'clusters',
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#f59e0b', // Orange for small clusters
            10,
            '#f97316', // Darker orange for medium
            30,
            '#ea580c'  // Dark orange for large
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,  // Base size
            10,
            30,  // Medium clusters
            30,
            40   // Large clusters
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        }
      });

      // ✅ Add cluster count label
      map.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 14
        },
        paint: {
          'text-color': '#ffffff'
        }
      });

      // ✅ Add individual venue layer (unclustered points)
      // ✅ Add individual venue layer (unclustered points)
      map.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        filter: ['!', ['has', 'point_count']], // Only show unclustered points
        layout: {
          // Icon settings
          'icon-image': [
            'match',
            ['get', 'icon'],
            ...Object.entries(ICON_MAP).flatMap(([key, file]) => [key, file.replace('.png', '')]),
            'default_pin' // fallback
          ],
          // ✅ DRAMATIC zoom scaling - much more noticeable
          'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 0.15,    // Tiny at zoom 8
            10, 0.25,   // Very small at zoom 10
            12, 0.4,    // Small-medium at zoom 12
            14, 0.6,    // Medium at zoom 14
            16, 0.85,   // Large at zoom 16
            18, 1.0     // Full size at zoom 18+
          ],
          // ✅ Show ALL icons - no hiding due to overlap
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-padding': 0,
          // ✅ ENHANCED: Show venue names at lower zoom levels
          // Format: [step, ['zoom'], default_value, breakpoint1, value1, breakpoint2, value2, ...]
          'text-field': ['step', ['zoom'], '', 14, ['get', 'name']],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            14, 9,     // Small text at zoom 14
            15, 10,    // Slightly larger at zoom 15
            16, 11,    // Medium at zoom 16
            18, 13     // Larger at zoom 18+
          ],
          'text-offset': [0, 1.2],
          'text-anchor': 'top',
          'text-optional': true,
          'text-allow-overlap': false,  // Allow some overlap for names
          'text-ignore-placement': false // But respect icon placement
        },
        paint: {
          'icon-opacity': 1,
          'text-color': '#333',
          'text-halo-color': '#fff',
          'text-halo-width': 1.5
        }
      });
      
      // ✅ WM WiFi pink glow layer
      if (!map.getLayer('wm-wifi-glow')) {
        map.addLayer({
          id: 'wm-wifi-glow',
          type: 'circle',
          source: sourceId,
          filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'wm_wifi'], 1]],
          paint: {
            'circle-color': '#FF00FF',
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 8, 14, 18, 16, 28],
            'circle-opacity': 0.55,
            'circle-stroke-color': '#FF1493',
            'circle-stroke-width': 2,
            'circle-stroke-opacity': 0.9,
          }
        }, layerId); // insert BELOW venue icons so icons appear on top
      }

      console.log('✅ Added native venue layer');
    }
  }, []);
  
  // Setup click handler
  const setupClickHandler = useCallback((map) => {
    const layerId = 'venues-layer';
    const clusterLayerId = 'clusters';
    
    // Handle cluster clicks - zoom in to expand
    map.on('click', clusterLayerId, (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [clusterLayerId] });
      if (features.length > 0) {
        const clusterId = features[0].properties.cluster_id;
        const source = map.getSource('venues-source');
        
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          
          map.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom + 0.5,
            duration: 500
          });
        });
      }
    });
    
    // Change cursor on cluster hover
    map.on('mouseenter', clusterLayerId, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    
    map.on('mouseleave', clusterLayerId, () => {
      map.getCanvas().style.cursor = '';
    });
    
    // Change cursor on venue hover
    map.on('mousemove', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
      map.getCanvas().style.cursor = features.length > 0 ? 'pointer' : '';
    });
    
    // Handle venue click
    map.on('click', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: [layerId] });
      console.log('🗺️ Map click - found', features.length, 'venue features at point');
      
      if (features.length > 0) {
        const feature = features[0];
        const venueId = feature.properties.id;
        const venueSlug = feature.properties.slug;
        // Use slug when available (new URLs), fall back to ObjectId (backwards compat)
        const venueIdentifier = venueSlug || venueId;

        console.log('🎯 Venue clicked:', feature.properties.name, 'slug:', venueSlug, 'ID:', venueId);

        if (onVenueClickRef.current) {
          onVenueClickRef.current(venueIdentifier);
        }
      }
    });
  }, []); // ✅ No dependencies - uses ref internally
  
  // Main effect - initialize layer when map and venues are ready
  useEffect(() => {
    console.log('🔍 NativeVenueLayer effect:', { 
      hasMapRef: !!mapRef?.current, 
      isMapLoaded, 
      venuesLength: venues?.length 
    });
    
    if (!mapRef?.current || !venues?.length) {
      console.log('⏳ NativeVenueLayer waiting for:', {
        mapRef: !mapRef?.current ? 'missing' : 'ok',
        venues: !venues?.length ? 'empty' : venues.length
      });
      return;
    }
    
    const map = mapRef.current.getMap();
    if (!map) {
      console.log('❌ Could not get map instance');
      return;
    }
    
    // Check if map style is loaded (more reliable than isMapLoaded prop)
    const isStyleLoaded = map.isStyleLoaded();
    console.log('🗺️ Map style loaded:', isStyleLoaded, 'isMapLoaded prop:', isMapLoaded);
    
    if (!isStyleLoaded && !isMapLoaded) {
      console.log('⏳ Waiting for map style to load...');
      // Add one-time listener for style load
      const handleStyleLoad = () => {
        console.log('✅ Map style now loaded, re-triggering effect');
        // Re-run the effect by updating state (will be handled naturally by React)
      };
      map.once('style.load', handleStyleLoad);
      return;
    }
    
    console.log('✅ NativeVenueLayer initializing with', venues.length, 'venues');
    
    const initializeLayer = async () => {
      try {
        console.log('🚀 Starting initializeLayer...');
        
        // Load icons first
        await loadIcons(map);
        console.log('📦 Icons loaded');
        
        // Convert venues to GeoJSON
        const geoJSON = venuesToGeoJSON(venues, wmSlugsSet);
        console.log(`📍 Creating GeoJSON with ${geoJSON.features.length} venues`);

        // Add/update layer
        updateVenueLayer(map, geoJSON, wmSlugsSet);
        console.log('🗺️ Layer updated');
        
        // ✅ FIXED: Setup click handler only once, after layer is added
        if (!clickHandlerAddedRef.current && map.getLayer('venues-layer')) {
          setupClickHandler(map);
          clickHandlerAddedRef.current = true;
          console.log('✅ Click handler added for venues-layer');
        }
      } catch (error) {
        console.error('❌ Error in initializeLayer:', error);
      }
    };
    
    initializeLayer();
  }, [mapRef, isMapLoaded, venues, wmSlugsSet, loadIcons, updateVenueLayer, setupClickHandler]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapRef?.current) {
        const map = mapRef.current.getMap();
        if (map) {
          if (map.getLayer('cluster-count')) {
            map.removeLayer('cluster-count');
          }
          if (map.getLayer('clusters')) {
            map.removeLayer('clusters');
          }
          if (map.getLayer('wm-wifi-glow')) {
            map.removeLayer('wm-wifi-glow');
          }
          if (map.getLayer('venues-layer')) {
            map.removeLayer('venues-layer');
          }
          if (map.getSource('venues-source')) {
            map.removeSource('venues-source');
          }
        }
      }
      iconsLoadedRef.current = false;
      sourceAddedRef.current = false;
      clickHandlerAddedRef.current = false;
    };
  }, [mapRef]);
  
  return {
    isReady: iconsLoadedRef.current && sourceAddedRef.current
  };
};

export default useNativeVenueLayer;
